import type { CallDiagnosticSink } from './direct-call-diagnostics';
import {
  conversationEventBody,
  downloadConversationAttachment,
  uploadConversationAttachment,
} from './direct-call-attachment-http';
import type {
  AttachmentUpload,
  ConversationAttachment,
} from './direct-call-attachments';
import type {
  ConversationEvent,
  ConversationEventPage,
  HumanConversationApi,
} from './direct-call-events';
import type { DirectCallSpeech } from './direct-call-playback';
import type { SpeechStart, SpeechStatus } from './direct-call-speech-source';
import type { DirectCallThread } from './direct-call-session';

export type HumanCallRequest = (
  url: string,
  options: {
    method: string;
    signal: AbortSignal;
    redirect: 'error';
    credentials: 'omit';
    headers: Record<string, string>;
    body?: string;
    /** Longer platform timeout for attachment transfers (WI 35510); text calls omit it. */
    timeoutMs?: number;
  }
) => Promise<{
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
  /** Raw body bytes; required to download attachments. */
  arrayBuffer?(): Promise<ArrayBuffer>;
}>;

/** A raw platform fetch bypasses the bridge Agent-JWT HTTP interceptor. */
export class HumanConversationHttp implements HumanConversationApi {
  private readonly base: string;

  constructor(
    baseUrl: string,
    private readonly humanToken: (signal: AbortSignal) => Promise<string>,
    private readonly request: HumanCallRequest,
    private readonly deadlineMs = 15000
  ) {
    const url = new URL(baseUrl);
    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    )
      throw new Error('An HTTPS bridge API URL is required');
    if (!Number.isFinite(deadlineMs) || deadlineMs <= 0)
      throw new Error('A positive request deadline is required');
    this.base = url.href.replace(/\/$/, '');
  }

  diagnosticSink(appId: string): CallDiagnosticSink {
    const path = (id: string) => {
      if (!/^[a-f0-9]{32}$/i.test(id))
        throw new Error('Invalid diagnostics session');
      return `/conversations/diagnostics/sessions/${id}`;
    };
    return {
      create: async (signal) => {
        if (
          !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
            appId
          )
        )
          throw new Error('Invalid diagnostics application');

        const result = await this.json<{ sessionId: string }>(
          '/conversations/diagnostics/sessions',
          'POST',
          signal,
          { appId: appId.toLowerCase() }
        );
        path(result?.sessionId);
        return result.sessionId;
      },
      append: async (id, events, signal) => {
        if (!Array.isArray(events) || events.length < 1 || events.length > 32)
          throw new Error('Invalid diagnostics batch');
        await this.json(path(id) + '/events', 'POST', signal, {
          events: events.map(
            ({ sequence, stage, outcome, code, elapsedMs, durationMs }) => ({
              sequence,
              stage,
              outcome,
              code,
              elapsedMs,
              durationMs,
            })
          ),
        });
      },
      link: async (id, sequence, conversationId, eventTurnId, signal) => {
        if (
          !Number.isSafeInteger(sequence) ||
          sequence < 1 ||
          sequence > 512 ||
          !/^[a-zA-Z0-9_-]{1,64}$/.test(conversationId) ||
          !Number.isSafeInteger(eventTurnId) ||
          eventTurnId < 1
        )
          throw new Error('Invalid diagnostics link');
        await this.json(path(id) + '/link', 'POST', signal, {
          sequence,
          conversationId,
          eventTurnId,
        });
      },
    };
  }

  async listThreads(
    appId: string,
    signal: AbortSignal
  ): Promise<readonly DirectCallThread[]> {
    if (!appId.trim()) throw new Error('Application id is required');
    const rows = await this.json<unknown>(
      `/conversations?appId=${encodeURIComponent(appId)}`,
      'GET',
      signal
    );
    if (!Array.isArray(rows)) throw new Error('Invalid conversation list');
    const ids = new Set<string>();
    const threads: DirectCallThread[] = [];
    for (const row of rows) {
      if (!row || typeof row !== 'object')
        throw new Error('Invalid conversation metadata');
      // Existing bridge serializers use PascalCase or camelCase. Reject conflicts.
      const field = (name: string): unknown => {
        const pascal = name[0].toUpperCase() + name.slice(1);
        if (
          row[name] !== undefined &&
          row[pascal] !== undefined &&
          JSON.stringify(row[name]) !== JSON.stringify(row[pascal])
        )
          throw new Error('Invalid conversation metadata');
        return row[name] ?? row[pascal];
      };
      const id = field('id');
      const title = field('title');
      const updatedAt = field('updatedAt');
      const members = field('members');
      const managed = field('membersManaged');
      if (
        typeof id !== 'string' ||
        !/^[a-zA-Z0-9_-]+$/.test(id) ||
        ids.has(id) ||
        field('appId') !== appId ||
        typeof title !== 'string' ||
        typeof updatedAt !== 'string' ||
        !Number.isFinite(Date.parse(updatedAt)) ||
        !Array.isArray(members) ||
        members.length > 20 ||
        members.some(
          (member) => typeof member !== 'string' || !member.trim()
        ) ||
        new Set(members).size !== members.length ||
        (managed !== undefined && typeof managed !== 'boolean')
      )
        throw new Error('Invalid conversation metadata');
      ids.add(id);
      // Legacy Ask Ryn has a separate message store, not the ordered-call log.
      if (managed === true || members.length)
        threads.push({ id, title, updatedAt, members: [...members] });
    }
    return threads;
  }

  readEvents(
    id: string,
    after: number,
    signal: AbortSignal
  ): Promise<ConversationEventPage> {
    if (!Number.isSafeInteger(after) || after < 0)
      return Promise.reject(new Error('Invalid checkpoint'));
    return this.json(
      `${this.eventsPath(id)}?afterTurnId=${after}`,
      'GET',
      signal
    );
  }

  async listSeats(
    signal: AbortSignal
  ): Promise<{ InstanceId: string; Role: string; DisplayName?: string }[]> {
    const result = await this.json<{
      seats: { id: string; role: string; displayName?: unknown }[];
    }>('/conversations/seats', 'GET', signal);
    if (
      !result ||
      !Array.isArray(result.seats) ||
      result.seats.some(
        (seat) =>
          !seat ||
          typeof seat.id !== 'string' ||
          !seat.id.trim() ||
          typeof seat.role !== 'string'
      )
    )
      throw new Error('Invalid conversation roster');
    // US 35458: a registered display name, when the bridge has one.
    return result.seats.map((seat) => ({
      InstanceId: seat.id,
      Role: seat.role,
      ...(typeof seat.displayName === 'string' && seat.displayName.trim()
        ? { DisplayName: seat.displayName.trim() }
        : {}),
    }));
  }

  async create(appId: string, signal: AbortSignal): Promise<string> {
    if (!appId.trim()) throw new Error('Application id is required');
    const result = await this.json<{ Id?: string; id?: string }>(
      '/conversations',
      'POST',
      signal,
      { appId, title: 'Seat call' }
    );
    const id = result?.id ?? result?.Id;
    if (
      typeof id !== 'string' ||
      !/^[a-zA-Z0-9_-]+$/.test(id) ||
      (result.id !== undefined &&
        result.Id !== undefined &&
        result.id !== result.Id)
    )
      throw new Error('Invalid conversation acknowledgment');
    return id;
  }

  async setMembers(
    id: string,
    appId: string,
    members: readonly string[],
    signal: AbortSignal
  ): Promise<readonly string[]> {
    // Validate the identifier before composing a second conversation route.
    this.eventsPath(id);
    const result = await this.json<{
      conversationId: string;
      members: string[];
    }>(`/conversations/${encodeURIComponent(id)}/members`, 'PUT', signal, {
      appId,
      members,
    });
    if (
      !result ||
      result.conversationId !== id ||
      !Array.isArray(result.members) ||
      result.members.length !== members.length ||
      new Set(result.members).size !== members.length ||
      result.members.some(
        (member) => typeof member !== 'string' || !members.includes(member)
      )
    )
      throw new Error('Conversation membership was not confirmed');
    return [...result.members];
  }

  postEvent(
    id: string,
    content: string,
    signal: AbortSignal,
    to?: readonly string[],
    attachmentIds: readonly string[] = []
  ): Promise<ConversationEvent> {
    if (!content.trim() && !attachmentIds.length)
      return Promise.reject(new Error('Input is empty'));
    const body = conversationEventBody(content, to, attachmentIds);
    return this.json(this.eventsPath(id), 'POST', signal, body);
  }

  /** WI 35510: store a pending upload; a later postEvent claims it by id. */
  uploadAttachment(
    id: string,
    upload: AttachmentUpload,
    signal: AbortSignal
  ): Promise<ConversationAttachment> {
    return uploadConversationAttachment(
      (...args) => this.send(...args),
      this.conversationId(id),
      upload,
      signal
    );
  }

  /** WI 35510: the bytes of a claimed attachment, via the authorized proxy. */
  downloadAttachment(
    id: string,
    attachmentId: string,
    signal: AbortSignal
  ): Promise<ArrayBuffer> {
    return downloadConversationAttachment(
      (...args) => this.send(...args),
      this.conversationId(id),
      attachmentId,
      signal
    );
  }

  protected conversationId(id: string): string {
    this.eventsPath(id);
    return encodeURIComponent(id);
  }

  private eventsPath(id: string): string {
    if (!/^[a-zA-Z0-9_-]+$/.test(id))
      throw new Error('Invalid conversation id');
    return `/conversations/${encodeURIComponent(id)}/events`;
  }

  startSpeech(
    reply: DirectCallSpeech,
    bindingId: string,
    signal: AbortSignal
  ): Promise<SpeechStart> {
    this.eventsPath(reply.conversationId ?? '');
    if (!/^[a-zA-Z0-9_-]+$/.test(reply.messageId))
      throw new Error('Invalid speech message');
    return this.json(
      `/conversations/${reply.conversationId}/messages/${reply.messageId}/speech/start`,
      'POST',
      signal,
      {
        bindingId,
        turnId: reply.eventTurnId,
        maxChunkBytes: 32768,
        windowSize: 4,
      }
    );
  }

  async ackSpeech(
    id: string,
    stream: SpeechStart,
    bindingId: string,
    consumedSeq: number,
    signal: AbortSignal
  ): Promise<void> {
    const result = await this.json<{ accepted: boolean }>(
      this.speechPath(id, stream) + '/ack',
      'POST',
      signal,
      { bindingId, generation: stream.generation, consumedSeq }
    );
    if (result?.accepted !== true)
      throw new Error('Speech acknowledgment rejected');
  }

  async cancelSpeech(
    id: string,
    stream: SpeechStart,
    bindingId: string,
    signal: AbortSignal
  ): Promise<void> {
    await this.json(this.speechPath(id, stream) + '/cancel', 'POST', signal, {
      bindingId,
      generation: stream.generation,
    });
  }

  speechStatus(
    id: string,
    stream: SpeechStart,
    signal: AbortSignal
  ): Promise<SpeechStatus> {
    return this.json(this.speechPath(id, stream) + '/status', 'GET', signal);
  }

  private speechPath(id: string, stream: SpeechStart): string {
    this.eventsPath(id);
    if (!/^[a-f0-9]{32}$/.test(stream.streamId))
      throw new Error('Invalid speech stream');
    return `/conversations/${id}/speech/${stream.streamId}`;
  }

  replaySpeech(
    id: string,
    stream: SpeechStart,
    bindingId: string,
    fromSeq: number,
    signal: AbortSignal
  ): Promise<unknown[]> {
    if (!Number.isSafeInteger(fromSeq) || fromSeq < 0)
      throw new Error('Invalid speech sequence');
    const query = new URLSearchParams({
      bindingId,
      generation: stream.generation,
      fromSeq: String(fromSeq),
    });
    return this.json(
      `${this.speechPath(id, stream)}/chunks?${query}`,
      'GET',
      signal
    );
  }

  async challenge(connectionId: string, signal: AbortSignal): Promise<string> {
    const result = await this.json<{ challengeId: string }>(
      '/conversations/connections/challenge',
      'POST',
      signal,
      { connectionId }
    );
    if (!/^[a-f0-9]{32}$/.test(result?.challengeId))
      throw new Error('Invalid connection challenge');
    return result.challengeId;
  }

  async confirm(
    challengeId: string,
    nonce: string,
    signal: AbortSignal
  ): Promise<string> {
    const result = await this.json<{ bindingId: string }>(
      '/conversations/connections/confirm',
      'POST',
      signal,
      { challengeId, nonce }
    );
    if (result?.bindingId !== challengeId)
      throw new Error('Connection binding was not confirmed');
    return result.bindingId;
  }

  private async json<T>(
    path: string,
    method: string,
    signal: AbortSignal,
    body?: unknown
  ): Promise<T> {
    const response = await this.send(path, method, signal, body);
    if (!response.ok)
      throw new Error(`Conversation request failed (${response.status})`);
    // Do not expose server error bodies or credentials in user-visible diagnostics.
    return (await response.json()) as T;
  }

  protected async send(
    path: string,
    method: string,
    signal: AbortSignal,
    body?: unknown,
    deadlineMs = this.deadlineMs
  ): Promise<Awaited<ReturnType<HumanCallRequest>>> {
    const operation = new AbortController();
    const abort = () => operation.abort();
    signal.addEventListener('abort', abort, { once: true });
    let rejectAbort: () => void = () => undefined;
    const aborted = new Promise<never>((_, reject) => {
      rejectAbort = () =>
        reject(new Error('Conversation request cancelled or timed out'));
    });
    operation.signal.addEventListener('abort', rejectAbort, { once: true });
    const timer = setTimeout(abort, deadlineMs);
    try {
      if (signal.aborted) operation.abort();
      const work = async () => {
        if (operation.signal.aborted)
          throw new Error('Conversation request cancelled');
        const token = await this.humanToken(operation.signal);
        if (operation.signal.aborted)
          throw new Error('Conversation request cancelled');
        if (!token?.trim()) throw new Error('Microsoft sign-in is required');
        const response = await this.request(this.base + path, {
          method,
          signal: operation.signal,
          redirect: 'error',
          credentials: 'omit',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: body === undefined ? undefined : JSON.stringify(body),
          ...(deadlineMs === this.deadlineMs ? {} : { timeoutMs: deadlineMs }),
        });
        return response;
      };
      return await Promise.race([work(), aborted]);
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      operation.signal.removeEventListener('abort', rejectAbort);
    }
  }
}
